/*
  SiteShell — BEARTH's permanent spatial frame.
  Swiss survey-instrument precision meets Nairobi night movement: coordinate
  rails, a five-arm constellation, a non-generic navigation instrument and an
  editorial footer. The shell changes presentation only; routes, SEO and
  conversion endpoints stay untouched.
*/
import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import EmailCapture from "@/components/EmailCapture";
import { Clocks, LangSwitch } from "@/components/fx";
import { useI18n } from "@/lib/i18n";
import { useUrlSrc, withUtm } from "@/lib/attribution";
import { ARMS, SOCIALS, COLORS, CONTACT_EMAIL, assetUrl } from "@/lib/brand";

function routeActive(location: string, href: string) {
  return href === "/" ? location === "/" : location === href || location.startsWith(`${href}/`);
}

function SignatureField({ location }: { location: string }) {
  const code = location === "/" ? "ORIGIN" : location.replace(/^\//, "").split("/")[0].toUpperCase();
  return (
    <div className="signature-field" aria-hidden="true">
      <div className="signature-grid" />
      <div className="signature-orbit signature-orbit-a" />
      <div className="signature-orbit signature-orbit-b" />
      <div className="signature-cross signature-cross-a" />
      <div className="signature-cross signature-cross-b" />
      <span className="signature-coordinate signature-coordinate-a">47.5596° N · 7.5886° E</span>
      <span className="signature-coordinate signature-coordinate-b">1.2921° S · 36.8219° E</span>
      <span className="signature-route-code">BEARTH / {code}</span>
    </div>
  );
}

function ArmConstellation({ location }: { location: string }) {
  return (
    <aside className="arm-constellation" aria-label="BEARTH portfolio status">
      <span className="arm-axis" aria-hidden />
      {ARMS.map((arm, index) => {
        const active = routeActive(location, arm.path);
        return (
          <Link
            key={arm.id}
            href={arm.path}
            className={`arm-node ${active ? "active" : ""}`}
            style={{ "--node-colour": arm.color, "--node-index": index } as React.CSSProperties}
            aria-label={`${arm.name} — ${arm.status}`}
            aria-current={active ? "page" : undefined}
          >
            <span className="arm-node-dot" aria-hidden />
            <span className="arm-node-name">{arm.id}</span>
          </Link>
        );
      })}
    </aside>
  );
}

function Nav() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [location] = useLocation();
  const links = [
    ...ARMS.map((arm) => ({ label: t.nav[arm.id as keyof typeof t.nav], href: arm.path, color: arm.color })),
    { label: t.nav.log, href: "/log", color: COLORS.cream },
    { label: t.nav.about, href: "/about", color: COLORS.cream },
  ];

  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <>
      <nav aria-label="Main navigation" className={`instrument-nav ${open ? "menu-open" : ""}`}>
        <div className="instrument-nav-inner">
          <Link href="/" className="instrument-home" aria-label="BEARTH — Home">HOME</Link>

          <Link href="/" className="instrument-brand" aria-label="BEARTH — Home">
            <span className="instrument-mark" aria-hidden>
              <img src={assetUrl("/brand/logo-ring.webp")} alt="" width={34} height={34} decoding="sync" />
              <span className="instrument-mark-line" />
            </span>
            <span className="sr-only">BEARTH · BASEL ↔ NAIROBI</span>
          </Link>

          <div className="flex items-center gap-3 justify-self-end">
            <Link
              href="/audit"
              className="press hidden sm:inline-flex items-center rounded-full bg-[#F5C400] px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#0A0A0A] transition-opacity hover:opacity-85"
            >
              {t.home.auditTeaser.cta}
            </Link>
            <button
              type="button"
              className="instrument-menu-button"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              aria-controls="mobile-navigation"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="w-5 h-5" aria-hidden /> : <Menu className="w-5 h-5" aria-hidden />}
            </button>
          </div>
        </div>
      </nav>

      <button
        type="button"
        tabIndex={open ? 0 : -1}
        aria-label="Close navigation"
        className={`menu-backdrop ${open ? "open" : ""}`}
        onClick={() => setOpen(false)}
      />

      <div
        id="mobile-navigation"
        className={`mobile-instrument-menu ${open ? "open" : ""}`}
        aria-hidden={!open}
        role="dialog"
        aria-modal="true"
        aria-label="BEARTH navigation"
      >
        <div className="mobile-menu-head">
          <Clocks />
          <span>BEARTH · BASEL ↔ NAIROBI</span>
        </div>

        <div className="mobile-menu-links">
          {links.map((link, index) => {
            const active = routeActive(location, link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                tabIndex={open ? 0 : -1}
                className={`mobile-menu-link ${active ? "active" : ""}`}
                style={{ "--link-colour": link.color } as React.CSSProperties}
                aria-current={active ? "page" : undefined}
              >
                <span aria-hidden>{String(index + 1).padStart(2, "0")}</span>
                <strong>{link.label}</strong>
                <i aria-hidden />
              </Link>
            );
          })}
        </div>

        <div className="mobile-menu-conversion">
          <p className="kicker" style={{ color: COLORS.signal }}>{t.audit.label}</p>
          <Link href="/audit" tabIndex={open ? 0 : -1} className="cta press">
            {t.home.ctaAudit} <ArrowRight className="w-4 h-4" aria-hidden />
          </Link>
        </div>

        <div className="mobile-menu-foot">
          <LangSwitch />
          <Link href="/contact" tabIndex={open ? 0 : -1} className="menu-contact-link">
            {t.nav.contact} <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
          </Link>
        </div>
      </div>
    </>
  );
}

function Footer() {
  const { t } = useI18n();
  const urlSrc = useUrlSrc();
  return (
    <footer className="signature-footer">
      <div className="container">
        <div className="bead-strip" aria-hidden />
        <div className="footer-manifesto">
          <div>
            <p className="kicker">BEARTH OPERATING FIELD · 47.5596° N ↔ 1.2921° S</p>
            <h2 className="display">BE EARTH.<br /><span className="serif-accent">Stay grounded.</span></h2>
          </div>
          <p>{t.footer.claim}</p>
        </div>

        <div className="footer-grid">
          <section className="footer-signal">
            <div className="footer-signal-head">
              <span className="status status-live"><span className="pulse-dot" aria-hidden />BEARTH SIGNAL</span>
              <span>Direct intelligence. No feed dependency.</span>
            </div>
            <EmailCapture variant="inline" source="footer" />
          </section>

          <nav aria-label="Portfolio" className="footer-column">
            <p className="kicker">{t.footer.portfolio}</p>
            {ARMS.map((arm, index) => (
              <Link key={arm.id} href={arm.path} className="footer-index-link">
                <span aria-hidden>{String(index + 1).padStart(2, "0")}</span>{arm.name}
              </Link>
            ))}
          </nav>

          <nav aria-label="Channels" className="footer-column">
            <p className="kicker">{t.footer.channels}</p>
            {SOCIALS.map((social) => (
              <a
                key={social.label}
                href={social.label === "Gumroad" ? withUtm(social.href, urlSrc) : social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-index-link"
                aria-label={`${social.label} - opens in new window`}
              >
                <ArrowUpRight className="w-3 h-3" aria-hidden />{social.label}
              </a>
            ))}
          </nav>

          <nav aria-label="Direct and legal" className="footer-column">
            <p className="kicker">{t.footer.direct}</p>
            <Link href="/audit" className="footer-index-link">{t.audit.label}</Link>
            <Link href="/data" className="footer-index-link">{t.dataPage.label}</Link>
            <Link href="/log" className="footer-index-link">{t.log.label}</Link>
            <Link href="/contact" className="footer-index-link">{t.footer.contactLink}</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="footer-index-link">{CONTACT_EMAIL}</a>
          </nav>
        </div>

        <div className="footer-legal-line">
          <p>© {new Date().getFullYear()} SAIMEN BEARTH · HAMMERSTRASSE 196 · 4057 BASEL</p>
          <div>
            <Link href="/legal/imprint">{t.footer.imprint}</Link>
            <Link href="/legal/privacy">{t.footer.privacy}</Link>
            <Link href="/legal/terms">{t.footer.terms}</Link>
            <Link href="/legal/editorial-ai">{t.footer.editorialAi}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function SiteShell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return (
    <div className="site-stage min-h-screen flex flex-col bg-background text-foreground">
      <a href="#main" className="skip-link">Skip to content</a>
      <SignatureField location={location} />
      <Nav />
      <ArmConstellation location={location} />
      <main
        id="main"
        key={location}
        className={`route-stage flex-1 ${location === "/" ? "route-stage-home" : ""}`}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
}
